/**
 * ReportGenerator.js
 * Comprehensive PDF report generation for data quality assessments.
 * Uses the Noble Syntax / DataQuad design language — see ReportGenerator.css.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import './ReportGenerator.css';
import {
  FileText,
  Download,
  Upload,
  Palette,
  Eye,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  X,
  ChevronDown,
  ChevronUp,
  Loader,
  FileCheck,
  BarChart3,
  Users,
  Target,
  Trash2,
  RefreshCw
} from 'lucide-react';

// Preset color palettes (output PDF colors).
// Curated set: Ocean Deep + 5 designer palettes from arc.graphique color
// stories. Two-color palettes use deeper hue as `primary` (executive summary
// metrics table, cover page) and lighter as `secondary` (subsection / quality
// findings table headers). Accent rotates within each palette's hue family so
// the Cleaning Summary header doesn't clash. Warning/danger stay constant
// (amber / red) — they carry semantic meaning the user shouldn't override.
const COLOR_PALETTES = {
  ocean: {
    name: 'Ocean Deep',
    primary: '#0369A1', secondary: '#0284C7', accent: '#06B6D4',
    warning: '#F59E0B', danger: '#EF4444', background: '#F0F9FF',
    text: '#0C4A6E', muted: '#7DD3FC'
  },
  darkSlate: {
    name: 'Dark Slate Gray',
    primary: '#39544B',     // Dark Slate Gray
    secondary: '#5C7A6F',   // lighter slate-green for subsection headers
    accent: '#E8D5A8',      // warm cream pulled from image typography
    warning: '#F59E0B', danger: '#EF4444', background: '#F5F2EA',
    text: '#1F302A',        // very dark forest
    muted: '#8FA095'        // mid green-gray
  },
  slateBlue: {
    name: 'Slate & Glacier',
    primary: '#619BB6',     // Slate Blue
    secondary: '#BAD7E1',   // Glacier
    accent: '#2C5F7A',      // deeper teal as contrast accent
    warning: '#F59E0B', danger: '#EF4444', background: '#F5FAFC',
    text: '#1F3F4F',
    muted: '#95B5C4'
  },
  bistre: {
    name: 'Bistre & Brown Sugar',
    primary: '#3D2C22',     // Bistre — very dark cocoa
    secondary: '#BB734B',   // Brown Sugar
    accent: '#D9A77A',      // lighter sugar tone
    warning: '#F59E0B', danger: '#EF4444', background: '#FBF6F0',
    text: '#2A1E18',
    muted: '#8B6750'
  },
  liberty: {
    name: 'Thistle & Liberty',
    primary: '#5B62B3',     // Liberty — deeper periwinkle reads better on big tables
    secondary: '#DEC2DB',   // Thistle
    accent: '#8B91D1',      // lighter purple for cleaning-summary header
    warning: '#F59E0B', danger: '#EF4444', background: '#FAF7F9',
    text: '#2D2F5C',
    muted: '#B0A3CC'
  },
  liver: {
    name: 'Liver & Tan',
    primary: '#6A3428',     // Liver — deep maroon-brown
    secondary: '#CFB882',   // Tan
    accent: '#B89A6A',      // deeper tan accent
    warning: '#F59E0B', danger: '#EF4444', background: '#FBF8F2',
    text: '#4A2419',
    muted: '#A0826A'
  }
};

const ReportGenerator = ({
  projectId,
  projectName = 'Data Quality Report',
  rawCheckResults = {},
  cleanedCheckResults = {},
  comparisonData = null,
  backcheckResults = null,
  mlResults = null,
  cleanedMLResults = null,
  rawDataInfo = null,
  cleanedDataInfo = null,
  qualityChecksRun = false,
  cleaningComplete = false,
  backcheckComplete = false,
  aiAssessmentComplete = false,
  onNavigate = null,
  onReportGenerated = null,
  savedReportGeneratorState = null,
  onSaveReportGeneratorState = null
}) => {
  const { isDarkMode } = useTheme();
  const fileInputRef = useRef(null);

  // Responsive
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const h = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1100;
  const isCompact = isMobile || isTablet;

  // State — persisted where available
  const [reportType, setReportType] = useState(savedReportGeneratorState?.reportType || 'detailed');
  // Default to Ocean Deep. If the user has a stale palette key in persisted
  // state (e.g. 'professional' / 'corporate' from before the palette refactor),
  // fall back to 'ocean' so we don't crash the generator with an undefined lookup.
  const [selectedPalette, setSelectedPalette] = useState(() => {
    const saved = savedReportGeneratorState?.selectedPalette;
    return (saved && COLOR_PALETTES[saved]) ? saved : 'ocean';
  });
  const [customColors, setCustomColors] = useState(savedReportGeneratorState?.customColors || null);
  const [useCustomColors, setUseCustomColors] = useState(savedReportGeneratorState?.useCustomColors || false);
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(savedReportGeneratorState?.logoPreview || null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    appearance: true,
    content: true,
    preview: false
  });
  const [reportTitle, setReportTitle] = useState(savedReportGeneratorState?.reportTitle || projectName || 'Data Quality Assessment Report');
  const [reportSubtitle, setReportSubtitle] = useState(savedReportGeneratorState?.reportSubtitle || '');
  const [authorName, setAuthorName] = useState(savedReportGeneratorState?.authorName || '');
  const [organizationName, setOrganizationName] = useState(savedReportGeneratorState?.organizationName || '');

  const API_BASE = process.env.REACT_APP_API_URL || 'https://api.dataquadapp.com';

  // Persistence helper
  const saveCurrentState = useCallback(() => {
    if (onSaveReportGeneratorState) {
      const stateToSave = {
        reportType, selectedPalette, customColors, useCustomColors,
        logoPreview, reportTitle, reportSubtitle, authorName, organizationName,
        savedAt: new Date().toISOString()
      };
      onSaveReportGeneratorState(stateToSave);
    }
  }, [reportType, selectedPalette, customColors, useCustomColors, logoPreview,
      reportTitle, reportSubtitle, authorName, organizationName, onSaveReportGeneratorState]);

  const currentColors = useCustomColors && customColors ? customColors : COLOR_PALETTES[selectedPalette];
  const canGenerateReport = qualityChecksRun;

  const completedStages = [
    { name: 'Quality Checks',       complete: qualityChecksRun,       required: true  },
    { name: 'Data Cleaning',        complete: cleaningComplete,       required: false },
    { name: 'Back-check Analysis',  complete: backcheckComplete,      required: false },
    { name: 'AI Assessment',        complete: aiAssessmentComplete,   required: false },
  ];
  const completedCount = completedStages.filter(s => s.complete).length;

  // --------------------------------------------------------------
  // Logo handling
  // --------------------------------------------------------------
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
        setError('Please upload a PNG or JPG image');
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setError('Logo file must be less than 2MB');
        return;
      }
      setLogo(file);
      setError(null);
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };
  const removeLogo = () => {
    setLogo(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCustomColorChange = (colorKey, value) => {
    setCustomColors(prev => ({
      // Spread from the currently selected palette as the starting point —
      // matches what the user sees in the preview before they tweak anything.
      // (Previously hardcoded to a 'professional' palette that no longer exists.)
      ...(prev || COLOR_PALETTES[selectedPalette] || COLOR_PALETTES.ocean),
      [colorKey]: value
    }));
  };

  useEffect(() => {
    if (useCustomColors && !customColors) {
      setCustomColors({ ...COLOR_PALETTES[selectedPalette] });
    }
  }, [useCustomColors, selectedPalette, customColors]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // --------------------------------------------------------------
  // Report generation (unchanged)
  // --------------------------------------------------------------
  const generateReport = async () => {
    if (!canGenerateReport) {
      setError('Please complete quality checks before generating a report');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      let logoBase64 = '';
      if (logo) {
        logoBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(logo);
        });
      }

      const payload = {
        project_id: projectId,
        report_type: reportType,
        report_title: reportTitle,
        report_subtitle: reportSubtitle,
        author_name: authorName,
        organization_name: organizationName,
        colors: currentColors,
        logo_base64: logoBase64,
        raw_check_results: rawCheckResults || {},
        cleaned_check_results: cleanedCheckResults || {},
        comparison_data: comparisonData || {},
        backcheck_results: backcheckResults || {},
        ml_results: mlResults || {},
        cleaned_ml_results: cleanedMLResults || {},
        raw_data_info: rawDataInfo || {},
        cleaned_data_info: cleanedDataInfo || {},
        workflow_status: {
          qualityChecksRun, cleaningComplete, backcheckComplete, aiAssessmentComplete
        }
      };

      let userEmail = '';
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        userEmail = userInfo.email || localStorage.getItem('userEmail') || '';
      } catch (e) {
        userEmail = localStorage.getItem('userEmail') || '';
      }

      const response = await fetch(`${API_BASE}/api/reports/generate-json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': userEmail
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to generate report');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportTitle.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      if (onReportGenerated) {
        onReportGenerated({
          reportType,
          reportTitle,
          generatedAt: new Date().toISOString()
        });
      }

      setTimeout(() => saveCurrentState(), 100);
    } catch (err) {
      console.error('Report generation error:', err);
      setError(err.message || 'Failed to generate report');
    } finally {
      setIsGenerating(false);
    }
  };

  // --------------------------------------------------------------
  // Render
  // --------------------------------------------------------------
  const reportTypes = [
    {
      id: 'executive', icon: Users, title: 'Executive Summary',
      desc: 'High-level overview with key metrics and grades. Best for stakeholders and management.',
      chips: ['~3–5 pages', 'key metrics', 'grades', 'recommendations'],
    },
    {
      id: 'detailed', icon: BarChart3, title: 'Detailed Report',
      desc: 'Comprehensive analysis covering all 13 detectors, cleaning operations impact, and field-operations recommendations. Best for data teams and field supervisors.',
      chips: ['~12–18 pages', 'all detectors', 'coaching insights', 'cleaning ops', 'recommendations'],
    },
  ];

  return (
    <div
      className={`report-generator-page ${isDarkMode ? 'dark' : 'light'}`}
      style={{
        width: '100%',
        minHeight: '100%',
        padding: isCompact ? '20px 14px 40px' : '32px 28px 40px',
        marginLeft: isCompact ? 0 : '0',
        fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <div className="dqr-header">
          <div className="dqr-header-icon">
            <FileText size={isMobile ? 20 : 24} />
          </div>
          <h1 className="dqr-title">Report Generator</h1>
        </div>
        <p className="dqr-subtitle">
          Generate comprehensive PDF reports of your data quality assessment
        </p>

        {/* Error banner */}
        {error && (
          <div className="dqr-error">
            <AlertCircle size={18} />
            <span>{error}</span>
            <button className="dqr-error-close" onClick={() => setError(null)}>
              <X size={16} />
            </button>
          </div>
        )}

        <div className="dqr-grid">
          {/* ==================== MAIN COLUMN ==================== */}
          <div className="dqr-main">

            {/* Report Configuration */}
            <div className="dqr-card with-nodes">
              <span className="cn-bl" /><span className="cn-br" />
              <div className="dqr-card-header" onClick={() => toggleSection('content')}>
                <div className="dqr-card-header-title">
                  <div className="dqr-card-icon">
                    <FileCheck size={16} />
                  </div>
                  Report Configuration
                </div>
                <span className="dqr-chevron">
                  {expandedSections.content ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </span>
              </div>

              {expandedSections.content && (
                <div className="dqr-card-body">
                  <div className="dqr-kicker">report type</div>
                  <div className="dqr-type-grid">
                    {reportTypes.map(opt => {
                      const Icon = opt.icon;
                      const active = reportType === opt.id;
                      return (
                        <div
                          key={opt.id}
                          className={`dqr-type-card ${active ? 'active' : ''}`}
                          onClick={() => setReportType(opt.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setReportType(opt.id); }}
                        >
                          <div className="dqr-type-head">
                            <Icon size={18} className="dqr-type-icon" />
                            <span className="dqr-type-title">{opt.title}</span>
                          </div>
                          <p className="dqr-type-desc">{opt.desc}</p>
                          <div className="dqr-type-chips">
                            {opt.chips.map(chip => (
                              <span key={chip} className="dqr-chip">{chip}</span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="dqr-kicker" style={{ marginTop: 22 }}>report details</div>
                  <div className="dqr-form-grid" style={{ marginTop: 0 }}>
                    <div>
                      <label className="dqr-label">Report Title</label>
                      <input
                        className="dqr-input"
                        type="text"
                        value={reportTitle}
                        onChange={(e) => setReportTitle(e.target.value)}
                        placeholder="Data Quality Assessment Report"
                      />
                    </div>
                    <div>
                      <label className="dqr-label">Subtitle (optional)</label>
                      <input
                        className="dqr-input"
                        type="text"
                        value={reportSubtitle}
                        onChange={(e) => setReportSubtitle(e.target.value)}
                        placeholder="e.g., Q4 2024 Survey Data"
                      />
                    </div>
                    <div>
                      <label className="dqr-label">Author Name (optional)</label>
                      <input
                        className="dqr-input"
                        type="text"
                        value={authorName}
                        onChange={(e) => setAuthorName(e.target.value)}
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="dqr-label">Organization (optional)</label>
                      <input
                        className="dqr-input"
                        type="text"
                        value={organizationName}
                        onChange={(e) => setOrganizationName(e.target.value)}
                        placeholder="Your organization"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Appearance */}
            <div className="dqr-card">
              <div className="dqr-card-header" onClick={() => toggleSection('appearance')}>
                <div className="dqr-card-header-title">
                  <div className="dqr-card-icon">
                    <Palette size={16} />
                  </div>
                  Appearance
                </div>
                <span className="dqr-chevron">
                  {expandedSections.appearance ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </span>
              </div>

              {expandedSections.appearance && (
                <div className="dqr-card-body">
                  <div className="dqr-kicker">logo (optional)</div>
                  {!logoPreview ? (
                    <div
                      className="dqr-logo-dropzone"
                      onClick={() => fileInputRef.current?.click()}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                    >
                      <Upload size={28} color={isDarkMode ? '#6b6862' : '#908c82'} />
                      <p className="dqr-logo-dropzone-text">Click to upload logo</p>
                      <p className="dqr-logo-dropzone-hint">png or jpg · max 2MB</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={handleLogoUpload}
                        style={{ display: 'none' }}
                      />
                    </div>
                  ) : (
                    <div className="dqr-logo-preview">
                      <img src={logoPreview} alt="Logo preview" className="dqr-logo-img" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontWeight: 500, fontSize: 13, color: isDarkMode ? '#f0ede4' : '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {logo?.name || 'Uploaded logo'}
                        </p>
                        {logo?.size && (
                          <p style={{ margin: '3px 0 0', fontSize: 11, fontWeight: 500, letterSpacing: '0.06em', textTransform: 'lowercase', color: isDarkMode ? '#6b6862' : '#908c82' }}>
                            {(logo.size / 1024).toFixed(1)} kb
                          </p>
                        )}
                      </div>
                      <button className="dqr-cta-secondary" onClick={removeLogo}>
                        <Trash2 size={13} />
                        Remove
                      </button>
                    </div>
                  )}

                  {/* Color palette */}
                  <div className="dqr-palette-head" style={{ marginTop: 22 }}>
                    <div className="dqr-kicker" style={{ margin: 0 }}>color palette</div>
                    <label className="dqr-palette-toggle">
                      <input
                        type="checkbox"
                        checked={useCustomColors}
                        onChange={(e) => setUseCustomColors(e.target.checked)}
                      />
                      Use custom colors
                    </label>
                  </div>

                  {!useCustomColors ? (
                    <div className="dqr-palette-grid">
                      {Object.entries(COLOR_PALETTES).map(([key, palette]) => {
                        const active = selectedPalette === key;
                        return (
                          <div
                            key={key}
                            className="dqr-palette-card"
                            onClick={() => setSelectedPalette(key)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedPalette(key); }}
                            style={active ? {
                              borderColor: palette.primary,
                              backgroundColor: isDarkMode ? `${palette.primary}25` : `${palette.primary}12`,
                            } : undefined}
                          >
                            <div
                              className="dqr-palette-bar"
                              style={{ background: `linear-gradient(90deg, ${palette.primary} 0%, ${palette.secondary} 60%, ${palette.accent} 100%)` }}
                            />
                            <div className="dqr-palette-dots">
                              <div className="dqr-palette-dot" style={{ backgroundColor: palette.primary }} />
                              <div className="dqr-palette-dot" style={{ backgroundColor: palette.secondary }} />
                              <div className="dqr-palette-dot" style={{ backgroundColor: palette.accent }} />
                            </div>
                            <div className="dqr-palette-name">{palette.name}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="dqr-custom-panel">
                      {[
                        { key: 'primary',   label: 'Primary Color'   },
                        { key: 'secondary', label: 'Secondary Color' },
                        { key: 'accent',    label: 'Accent Color'    },
                        { key: 'text',      label: 'Text Color'      },
                      ].map(({ key, label }) => (
                        <div key={key} className="dqr-color-row">
                          <span className="dqr-color-label">{label}</span>
                          <input
                            type="color"
                            value={customColors?.[key] || COLOR_PALETTES[selectedPalette][key]}
                            onChange={(e) => handleCustomColorChange(key, e.target.value)}
                            style={{ width: 52, height: 28, padding: 2, borderRadius: 2, border: 'none', cursor: 'pointer' }}
                          />
                        </div>
                      ))}
                      <button
                        className="dqr-cta-secondary"
                        onClick={() => setCustomColors({ ...COLOR_PALETTES[selectedPalette] })}
                        style={{ width: '100%', marginTop: 6 }}
                      >
                        <RefreshCw size={13} />
                        Reset to {COLOR_PALETTES[selectedPalette].name}
                      </button>
                    </div>
                  )}

                  {/* Preview strip */}
                  <div className="dqr-preview-strip">
                    <div className="dqr-kicker" style={{ marginBottom: 10 }}>preview</div>
                    <div className="dqr-preview-pills">
                      <span className="dqr-preview-pill" style={{ backgroundColor: currentColors.primary }}>Primary</span>
                      <span className="dqr-preview-pill" style={{ backgroundColor: currentColors.secondary }}>Secondary</span>
                      <span className="dqr-preview-pill" style={{ backgroundColor: currentColors.accent }}>Accent</span>
                    </div>
                    <p className="dqr-preview-text" style={{ color: isDarkMode ? '#a8a49a' : currentColors.text }}>
                      Sample text in report body color
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ==================== SIDE COLUMN ==================== */}
          <div className="dqr-side">
            {/* Report Content Status */}
            <div className="dqr-card">
              <div className="dqr-card-header" style={{ cursor: 'default' }}>
                <div className="dqr-card-header-title">
                  <div className="dqr-card-icon">
                    <Target size={16} />
                  </div>
                  Report Content Status
                </div>
              </div>
              <div className="dqr-card-body">
                <div className="dqr-status-list">
                  {completedStages.map((stage, i) => (
                    <div key={i} className={`dqr-status-item ${stage.complete ? 'complete' : ''}`}>
                      {stage.complete ? (
                        <CheckCircle size={16} color={isDarkMode ? '#34d399' : '#047857'} />
                      ) : (
                        <AlertTriangle size={16} color={stage.required ? '#ef4444' : '#b45309'} />
                      )}
                      <div className="dqr-status-item-text">
                        <div className="dqr-status-item-name">{stage.name}</div>
                        <div className="dqr-status-item-sub">
                          {stage.complete ? 'included in report' : (stage.required ? 'required' : 'optional')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {!canGenerateReport && (
                  <div className="dqr-status-warn">
                    <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                    <div><strong>Note:</strong> Complete quality checks to generate a report.</div>
                  </div>
                )}
              </div>
            </div>

            {/* Generate CTA — desktop-only (sticky bar covers compact) */}
            {!isCompact && (
              <div className="dqr-card">
                <div className="dqr-card-body">
                  <button
                    className="dqr-cta"
                    onClick={generateReport}
                    disabled={!canGenerateReport || isGenerating}
                  >
                    {isGenerating ? (
                      <>
                        <Loader size={16} className="dqr-spinner" />
                        <span>Generating Report…</span>
                      </>
                    ) : (
                      <>
                        <span>Generate PDF Report</span>
                        <Download size={14} />
                      </>
                    )}
                  </button>
                  <p className="dqr-cta-note">
                    {reportType === 'executive' ? 'executive summary · ~3–5 pages' : 'detailed report · ~12–18 pages · 13 detectors covered'}
                  </p>
                </div>
              </div>
            )}

            {/* Report contents */}
            <div className="dqr-card">
              <div className="dqr-card-header" onClick={() => toggleSection('preview')}>
                <div className="dqr-card-header-title">
                  <div className="dqr-card-icon">
                    <Eye size={16} />
                  </div>
                  Report Contents
                </div>
                <span className="dqr-chevron">
                  {expandedSections.preview ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </span>
              </div>
              {expandedSections.preview && (
                <div className="dqr-card-body">
                  <div className="dqr-kicker">sections included</div>
                  <ul className="dqr-contents">
                    <li>Cover Page{logo ? ' (with logo)' : ''}</li>
                    <li>Executive Summary</li>
                    <li>Data Overview</li>
                    {qualityChecksRun   && (
                      <li>
                        Quality Findings
                        {reportType === 'detailed' && (
                          <span className="dqr-contents-sub">
                            {' '}— missingness, duplicates, outliers, duration,
                            geolocation, survey flow, text clustering,
                            sentinel phrases, gated fields, probe depth, straight-lining
                          </span>
                        )}
                      </li>
                    )}
                    {cleaningComplete   && (
                      <li>
                        Cleaning Summary
                        {reportType === 'detailed' && (
                          <span className="dqr-contents-sub">
                            {' '}— rows / columns dropped, flag columns added, Standardize impact
                          </span>
                        )}
                      </li>
                    )}
                    {cleaningComplete   && <li>Before/After Comparison</li>}
                    {backcheckComplete  && <li>Back-check Results</li>}
                    {aiAssessmentComplete && <li>AI/ML Insights</li>}
                    <li>Recommendations</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky action bar — compact viewports only */}
      {isCompact && (
        <div className="dqr-sticky-bar">
          <div className="dqr-sticky-inner">
            <div className={`dqr-sticky-status ${canGenerateReport ? 'ready' : 'pending'}`}>
              {canGenerateReport ? (
                <>
                  <CheckCircle size={13} />
                  {completedCount}/{completedStages.length} ready
                </>
              ) : (
                <>
                  <AlertTriangle size={13} />
                  run quality checks
                </>
              )}
            </div>
            <button
              className="dqr-cta"
              onClick={generateReport}
              disabled={!canGenerateReport || isGenerating}
            >
              {isGenerating ? (
                <>
                  <Loader size={14} className="dqr-spinner" />
                  <span>Generating…</span>
                </>
              ) : (
                <>
                  <span>Generate {reportType === 'executive' ? 'Summary' : 'Report'}</span>
                  <Download size={13} />
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportGenerator;